import { useState } from "react";
import { Send, TestTube, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Push Broadcast composer — admin-only form that sits on /admin/monetization.
 * Calls POST /api/admin/push/broadcast (or /test for a self-send). Shows the
 * audience size + last-send result so the admin can gauge reach at a glance.
 */
export default function PushBroadcastPanel() {
  const [title, setTitle]     = useState("The Ember calls you back");
  const [body, setBody]       = useState("A new run awaits — your embers have returned.");
  const [tag, setTag]         = useState("");            // "" = all subscribers
  const [url, setUrl]         = useState("/dashboard");
  const [busy, setBusy]       = useState(false);
  const [preview, setPreview] = useState(null);   // {audience}
  const [result, setResult]   = useState(null);   // {audience, sent, failed, deactivated}
  const [error, setError]     = useState(null);

  const doPreview = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post("/admin/push/broadcast", {
        title, body, tag: tag || null, url: url || "/dashboard", dry_run: true,
      });
      setPreview(r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (busy) return;
    if (!title.trim() || !body.trim()) {
      setError("Title and body are required.");
      return;
    }
    if (!window.confirm(`Blast to ${preview?.audience ?? "all"} subscribers? This cannot be undone.`)) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post("/admin/push/broadcast", {
        title, body, tag: tag || null, url: url || "/dashboard", dry_run: false,
      });
      setResult(r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Broadcast failed.");
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    if (busy) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post("/admin/push/test");
      setResult({ ...r, test_only: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Test send failed. Turn on NOTIFY ME on your dashboard first.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8" data-testid="push-broadcast-panel">
      <div className="text-[10px] tracking-[0.4em] gold-text mb-2 flex items-center gap-1">
        <Send size={12} /> PUSH BROADCAST · REACH THE FALLEN
      </div>
      <div className="panel p-5 space-y-4">
        {/* Row 1: title */}
        <div>
          <label className="text-[10px] tracking-[0.3em] ash-text block mb-1">TITLE</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            className="w-full bg-black/40 border border-white/10 focus:border-[#d4af37] px-3 py-2 text-sm text-[#f0e6d2] font-narrative outline-none"
            placeholder="The Ember calls you back"
            data-testid="push-broadcast-title"
          />
          <div className="text-[9px] tracking-widest ash-text mt-1 text-right">{title.length}/80</div>
        </div>

        {/* Row 2: body */}
        <div>
          <label className="text-[10px] tracking-[0.3em] ash-text block mb-1">BODY</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={200}
            rows={3}
            className="w-full bg-black/40 border border-white/10 focus:border-[#d4af37] px-3 py-2 text-sm text-[#f0e6d2] font-narrative outline-none resize-none"
            placeholder="A new run awaits — your embers have returned."
            data-testid="push-broadcast-body"
          />
          <div className="text-[9px] tracking-widest ash-text mt-1 text-right">{body.length}/200</div>
        </div>

        {/* Row 3: tag + url */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.3em] ash-text block mb-1">
              TAG FILTER (optional)
            </label>
            <input
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              maxLength={40}
              className="w-full bg-black/40 border border-white/10 focus:border-[#d4af37] px-3 py-2 text-sm text-[#f0e6d2] font-narrative outline-none"
              placeholder="empty = blast all subscribers"
              data-testid="push-broadcast-tag"
            />
            <div className="text-[9px] tracking-widest ash-text mt-1">
              Common tags: <span className="gold-text">d1-return</span> · <span className="gold-text">ember-rush</span> · <span className="gold-text">boss-defeat</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] ash-text block mb-1">
              CLICK-URL (deep link)
            </label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              maxLength={200}
              className="w-full bg-black/40 border border-white/10 focus:border-[#d4af37] px-3 py-2 text-sm text-[#f0e6d2] font-narrative outline-none"
              placeholder="/dashboard"
              data-testid="push-broadcast-url"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={doPreview}
            disabled={busy}
            className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors disabled:opacity-50"
            data-testid="push-broadcast-preview-btn"
          >
            <span className="text-[10px] tracking-[0.3em] ash-text">PREVIEW AUDIENCE</span>
          </button>
          <button
            type="button"
            onClick={doTest}
            disabled={busy}
            className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors disabled:opacity-50"
            title="Send to your own subscribed browser only"
            data-testid="push-broadcast-test-btn"
          >
            <TestTube size={13} />
            <span className="text-[10px] tracking-[0.3em] ash-text">TEST · SELF ONLY</span>
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={doSend}
            disabled={busy || !title.trim() || !body.trim()}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="push-broadcast-send-btn"
          >
            <Send size={13} />
            <span>{busy ? "SENDING…" : "SEND TO ALL"}</span>
          </button>
        </div>

        {/* Feedback */}
        {error && (
          <div
            className="panel p-3 border-red-500/40 flex items-start gap-2"
            data-testid="push-broadcast-error"
          >
            <AlertCircle size={14} className="blood-text flex-shrink-0 mt-0.5" />
            <div className="text-[11px] blood-text font-narrative">{error}</div>
          </div>
        )}
        {preview && !result && (
          <div className="panel p-3 border-[#d4af37]/40" data-testid="push-broadcast-preview-result">
            <div className="text-[10px] tracking-[0.3em] gold-text">DRY-RUN</div>
            <div className="text-sm text-[#f0e6d2] mt-1">
              <span className="font-heading text-2xl gold-text">{preview.audience}</span>
              <span className="ml-2 ash-text">subscribers match this filter.</span>
            </div>
          </div>
        )}
        {result && (
          <div
            className="panel p-3 border-[#7cffa0]/40 flex items-start gap-2"
            data-testid="push-broadcast-result"
          >
            <CheckCircle2 size={14} className="text-[#7cffa0] flex-shrink-0 mt-0.5" />
            <div className="text-[11px] font-narrative text-[#f0e6d2]">
              {result.test_only ? "TEST BLAST SENT · " : "BLAST SENT · "}
              audience <span className="gold-text">{result.audience}</span>, sent{" "}
              <span className="gold-text">{result.sent}</span>
              {result.failed > 0 && (
                <>, failed <span className="blood-text">{result.failed}</span></>
              )}
              {result.deactivated > 0 && (
                <>, deactivated <span className="ash-text">{result.deactivated}</span> dead endpoints</>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
